import type { FC } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Stack, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { NativeModal } from '@dhl/native-modal'
import { StarSolid } from '@dhl/icons'
import { useGetWhatsNewFeatures } from '@dhl/receiver-preference-api'
import { links } from '~/links.js'

type Props = {
  onCompleteInteraction: () => Promise<unknown>
}

export const AgreedPlacePhotoInteractionDialog: FC<Props> = ({ onCompleteInteraction }) => {
  const [open, setOpen] = useState<boolean>(true)
  const { data: { features } = {} } = useGetWhatsNewFeatures()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const handleConfirmClick = async () => {
    await onCompleteInteraction()
    setOpen(false)

    const preferences = features?.find(({ name }) => name === 'agreed-place-picture')?.preferences ?? []

    if (preferences.length === 1) {
      navigate(links.deliveryPreferenceDetailsLink({ addressId: preferences[0].addressId }))
    } else {
      navigate(links.preferencesLink())
    }
  }

  const handleCancelClick = async () => {
    onCompleteInteraction()
    setOpen(false)
  }

  return (
    <NativeModal open={open} onClose={handleCancelClick}>
      <NativeModal.Header onClose={handleCancelClick} />
      <NativeModal.Content>
        <Stack alignItems="center" data-test="agreed-place-photo-interaction-dialog">
          <StarSolid color="secondary" fontSize="large" sx={{ mb: 1 }} />
          <Typography variant="h4">{t('portal.interaction.dialog.agreedPlacePhoto.title')}</Typography>
          <Typography mt={2} textAlign="center">
            {t('portal.interaction.dialog.agreedPlacePhoto.description')}
          </Typography>
        </Stack>
      </NativeModal.Content>
      <NativeModal.Footer>
        <Button
          variant="contained"
          sx={{ mt: 3 }}
          fullWidth
          onClick={handleConfirmClick}
          data-test="agreed-place-photo-interaction-dialog-confirm"
        >
          {t('portal.interaction.dialog.agreedPlacePhoto.button')}
        </Button>
        <Button
          variant="text"
          sx={{ mt: 2 }}
          fullWidth
          onClick={handleCancelClick}
          data-test="agreed-place-photo-interaction-dialog-cancel"
        >
          {t('portal.interaction.dialog.agreedPlacePhoto.cancel')}
        </Button>
      </NativeModal.Footer>
    </NativeModal>
  )
}
